import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Brain, Plus, Search, Trash2, Pencil, ChevronRight, Loader2, Sparkles, Database,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { DocumentsLink } from '../../components/DocumentsLink';

const RISK_BADGE = {
  prohibited: { label: 'Proibido', color: 'bg-red-100 text-red-800 border-red-300' },
  high: { label: 'Alto Risco', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  limited: { label: 'Risco Limitado', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  minimal: { label: 'Risco Mínimo', color: 'bg-green-100 text-green-800 border-green-300' },
};

const SECTORS = [
  { value: 'infraestrutura_critica', label: 'Infraestrutura Crítica' },
  { value: 'educacao', label: 'Educação' },
  { value: 'emprego', label: 'Emprego' },
  { value: 'servicos_essenciais', label: 'Serviços Essenciais' },
  { value: 'lei_justica', label: 'Lei e Justiça' },
  { value: 'migracao', label: 'Migração' },
  { value: 'democracia', label: 'Democracia' },
  { value: 'saude', label: 'Saúde' },
  { value: 'outro', label: 'Outro' },
];

const DEPLOYER_ROLES = [
  { value: 'provider', label: 'Fornecedor (Provider)' },
  { value: 'deployer', label: 'Utilizador (Deployer)' },
  { value: 'importer', label: 'Importador' },
  { value: 'distributor', label: 'Distribuidor' },
];

const initialForm = {
  name: '', provider: '', version: '', ai_purpose: '',
  ai_sector: 'outro', ai_deployer_role: 'deployer',
  ai_deployment_date: '', technical_url: '',
};

export default function AiSystemsList() {
  const [systems, setSystems] = useState([]);
  const [totalCounts, setTotalCounts] = useState({ all: 0, ai_system: 0, gpai_model: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [isSaving, setIsSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = {};
        if (riskFilter) params.risk_level = riskFilter;
        if (sectorFilter) params.sector = sectorFilter;
        if (entityFilter) params.entity_type = entityFilter;
        const res = await aiActApi.listSystems(params);
        if (ignore) return;
        setSystems(Array.isArray(res?.data) ? res.data : []);
        // Also fetch total counts (no filters) for the tab pills
        try {
          const all = await aiActApi.listSystems({});
          if (ignore) return;
          const arr = Array.isArray(all?.data) ? all.data : [];
          const gpai = arr.filter(s => s.entity_type === 'gpai_model').length;
          setTotalCounts({ all: arr.length, gpai_model: gpai, ai_system: arr.length - gpai });
        } catch { /* non-blocking */ }
      } catch (err) {
        if (ignore) return;
        toast.error('Erro ao carregar sistemas IA');
        setSystems([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => { ignore = true; };
  }, [riskFilter, sectorFilter, entityFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (riskFilter) params.risk_level = riskFilter;
      if (sectorFilter) params.sector = sectorFilter;
      if (entityFilter) params.entity_type = entityFilter;
      const res = await aiActApi.listSystems(params);
      setSystems(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast.error('Erro ao carregar sistemas IA');
      setSystems([]);
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setIsSaving(true);
    try {
      const payload = { ...form, ai_deployment_date: form.ai_deployment_date || null };
      if (editing) {
        await aiActApi.updateSystem(editing.system_id, payload);
        toast.success('Sistema IA atualizado');
      } else {
        await aiActApi.createSystem(payload);
        toast.success('Sistema IA registado');
      }
      setShowForm(false); setEditing(null); setForm(initialForm); load();
    } catch { toast.error('Erro ao guardar'); }
    finally { setIsSaving(false); }
  };

  const handleEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '', provider: s.provider || '', version: s.version || '',
      ai_purpose: s.ai_purpose || '', ai_sector: s.ai_sector || 'outro',
      ai_deployer_role: s.ai_deployer_role || 'deployer',
      ai_deployment_date: s.ai_deployment_date || '',
      technical_url: s.technical_url || '',
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!delTarget) return;
    try {
      await aiActApi.deleteSystem(delTarget.system_id);
      toast.success('Sistema IA eliminado');
      setDelTarget(null); load();
    } catch { toast.error('Erro ao eliminar'); }
  };

  const handleSeedGpai = async () => {
    setSeeding(true);
    try {
      const res = await (await import('../../lib/api')).default.post('/seed-ai-gpai');
      toast.success(`Catálogo GPAI: ${res.data.created} criados, ${res.data.skipped} ignorados`);
      load();
    } catch { toast.error('Erro ao seed catálogo GPAI'); }
    finally { setSeeding(false); }
  };

  const filtered = systems.filter(s => {
    const q = search.toLowerCase();
    return !q || s.name?.toLowerCase().includes(q) || s.system_id?.toLowerCase().includes(q) || (s.provider || '').toLowerCase().includes(q);
  });

  const header = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={handleSeedGpai} disabled={seeding} data-testid="seed-gpai-btn">
        {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
        Importar catálogo GPAI
      </Button>
      <Button onClick={() => { setEditing(null); setForm(initialForm); setShowForm(true); }} data-testid="new-ai-system-btn">
        <Plus className="h-4 w-4 mr-2" /> Novo Sistema IA
      </Button>
    </div>
  );

  if (loading) return <Layout title="EU AI Act — Sistemas IA"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div></Layout>;

  return (
    <Layout title="EU AI Act — Sistemas IA" headerContent={header}>
      <div className="space-y-6" data-testid="ai-systems-container">

        {/* Segmented tabs: GPAI vs Custom Systems (Art. 51-56 vs. Art. 6+ Annex III) */}
        {(() => {
          const items = [
            { key: '', label: 'Todos', count: totalCounts.all, hint: '' },
            { key: 'ai_system', label: 'Sistemas Custom', count: totalCounts.ai_system, hint: 'Art. 6 + Anexo III' },
            { key: 'gpai_model', label: 'Modelos GPAI', count: totalCounts.gpai_model, hint: 'Art. 51-56' },
          ];
          return (
            <div className="flex items-center gap-2 flex-wrap" data-testid="ai-system-type-tabs">
              {items.map(it => (
                <button
                  key={it.key || 'all'}
                  data-testid={`ai-tab-${it.key || 'all'}`}
                  onClick={() => setEntityFilter(it.key)}
                  className={cn(
                    'group flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all',
                    entityFilter === it.key
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'
                  )}
                >
                  <span className="font-semibold text-sm">{it.label}</span>
                  <span className={cn(
                    'text-xs font-bold tabular-nums rounded-full px-2 py-0.5',
                    entityFilter === it.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  )}>{it.count}</span>
                  {it.hint && (
                    <span className={cn('text-[10px] uppercase tracking-wide', entityFilter === it.key ? 'text-indigo-100' : 'text-slate-400')}>
                      {it.hint}
                    </span>
                  )}
                </button>
              ))}
            </div>
          );
        })()}

        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Pesquisar por nome, ID ou fornecedor..." className="pl-10"
                  value={search} onChange={e => setSearch(e.target.value)}
                  data-testid="search-systems-input" />
              </div>
              <Select value={riskFilter || 'all'} onValueChange={v => setRiskFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-risk"><SelectValue placeholder="Nível de Risco" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os riscos</SelectItem>
                  <SelectItem value="prohibited">Proibido</SelectItem>
                  <SelectItem value="high">Alto Risco</SelectItem>
                  <SelectItem value="limited">Risco Limitado</SelectItem>
                  <SelectItem value="minimal">Risco Mínimo</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sectorFilter || 'all'} onValueChange={v => setSectorFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-sector"><SelectValue placeholder="Setor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os setores</SelectItem>
                  {SECTORS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {filtered.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="p-8 text-center">
              <Brain className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Nenhum sistema IA registado</h3>
              <p className="text-sm text-slate-500 mb-4">Comece por adicionar o seu primeiro sistema IA ou importe o catálogo de modelos GPAI conhecidos.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="systems-list">
            {filtered.map(s => {
              const riskCfg = s.ai_risk_level ? RISK_BADGE[s.ai_risk_level] : null;
              return (
                <Card key={s.system_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`system-${s.system_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                        <Brain className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-mono text-xs text-slate-400">{s.system_id}</span>
                              {s.entity_type === 'gpai_model' && (
                                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">GPAI</Badge>
                              )}
                              {riskCfg ? (
                                <Badge variant="outline" className={cn('text-xs', riskCfg.color)}>{riskCfg.label}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs bg-slate-100 text-slate-700">Não classificado</Badge>
                              )}
                              {s.ai_conformity_status === 'registered_eu' && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Registo EU ✓</Badge>
                              )}
                            </div>
                            <h3 className="font-medium text-slate-900 truncate">{s.name}</h3>
                            <p className="text-sm text-slate-500 mt-1 truncate">
                              {s.provider || 'Sem fornecedor'} {s.version && `• v${s.version}`}
                            </p>
                            {s.ai_purpose && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{s.ai_purpose}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Link to={`/ai-act/systems/${s.system_id}/classify`}>
                              <Button variant="outline" size="sm" data-testid={`classify-${s.system_id}`}>
                                <Sparkles className="h-4 w-4 mr-1" /> Classificar
                              </Button>
                            </Link>
                            <DocumentsLink module="asset" entityId={s.system_id} title={s.name} />
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} data-testid={`edit-${s.system_id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDelTarget(s)} data-testid={`delete-${s.system_id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Link to={`/ai-act/systems/${s.system_id}`}>
                              <Button variant="ghost" size="sm" data-testid={`open-${s.system_id}`}><ChevronRight className="h-4 w-4" /></Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Sistema IA' : 'Novo Sistema IA'}</DialogTitle>
            <DialogDescription>Registo no inventário (F1) — Regulamento UE 2024/1689</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Sistema *</Label>
              <Input id="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="form-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="provider">Fornecedor</Label>
                <Input id="provider" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} data-testid="form-provider" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Versão</Label>
                <Input id="version" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} data-testid="form-version" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ai_purpose">Finalidade do Sistema</Label>
              <Textarea id="ai_purpose" rows={3} value={form.ai_purpose} onChange={e => setForm({ ...form, ai_purpose: e.target.value })} data-testid="form-purpose" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Setor</Label>
                <Select value={form.ai_sector} onValueChange={v => setForm({ ...form, ai_sector: v })}>
                  <SelectTrigger data-testid="form-sector"><SelectValue /></SelectTrigger>
                  <SelectContent>{SECTORS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Papel na cadeia</Label>
                <Select value={form.ai_deployer_role} onValueChange={v => setForm({ ...form, ai_deployer_role: v })}>
                  <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPLOYER_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data de implementação</Label>
                <Input type="date" value={form.ai_deployment_date} onChange={e => setForm({ ...form, ai_deployment_date: e.target.value })} data-testid="form-deploy-date" />
              </div>
              <div className="space-y-2">
                <Label>URL doc. técnica</Label>
                <Input value={form.technical_url} onChange={e => setForm({ ...form, technical_url: e.target.value })} placeholder="https://..." data-testid="form-tech-url" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving} data-testid="save-system-btn">
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editing ? 'Guardar' : 'Registar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Sistema IA</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que pretende eliminar "{delTarget?.name}"? Esta ação também remove os riscos, documentação técnica e governança de dados associados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-system">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
